/**
 * Invoice PDF document — SightHost.
 *
 * Lifted from ledger-flow-ifrs-beacon `InvoiceProfessional.tsx` and adapted for
 * SightHost's no-VAT model:
 *   - Header reads "INVOICE" (not "TAX INVOICE") because SightHost is not VAT-registered.
 *   - When `company.vatRegistered = true` (future), header switches to "TAX INVOICE"
 *     and the VAT line in totals shows the tax amount.
 *   - Customer's own VAT number renders in the "Bill To" block when present
 *     (informational; helps the customer's own bookkeeping).
 *
 * VAT plumbing (`tax_amount`, `tax_rate` per line) is preserved so that switching
 * to VAT-registered is a single config flip — see memory/vat_model.md.
 */

import * as React from 'react';
import { Document, Page, View, Text, Image, Link, StyleSheet } from '@react-pdf/renderer';
import { documentStyles, formatPDFCurrency, formatPDFDate } from './styles';
import { PDFTable, type TableColumn } from './PDFTable';

/** Optional one-click PayFast checkout block, rendered above bank details on
 *  unpaid invoices. Mirrors the email "Pay R… by card" CTA so customers paying
 *  off a printed/emailed PDF can settle without opening the portal. */
export interface InvoicePDFPayNow {
  /** Public `/pay/:invoice_id?token=…` URL — no portal sign-in required. */
  url: string;
  /** Pre-formatted amount, e.g. "R129.00" — included in the button copy. */
  amountDisplay: string;
}

const payNowStyles = StyleSheet.create({
  box: {
    marginTop: 8,
    marginBottom: 12,
    padding: 10,
    borderWidth: 1,
    borderColor: '#F58C1E',
    borderRadius: 4,
    backgroundColor: '#FFF8EC',
  },
  title: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#212121',
    marginBottom: 4,
  },
  body: {
    fontSize: 9,
    color: '#1f2937',
    marginBottom: 6,
    lineHeight: 1.4,
  },
  link: {
    fontSize: 9,
    color: '#212121',
    fontFamily: 'Helvetica-Bold',
    textDecoration: 'underline',
  },
  hint: {
    fontSize: 7,
    color: '#6b7280',
    marginTop: 4,
  },
});

/** The issuing company (SightHost). Sourced from `siteConfig` at render time. */
export interface InvoicePDFCompany {
  companyName: string;
  legalName?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  phone?: string;
  email?: string;
  website?: string;
  registrationNumber?: string;
  /** Empty string when not VAT-registered. */
  vatNumber?: string;
  /** Drives "INVOICE" vs "TAX INVOICE" header text. */
  vatRegistered: boolean;
  /** Required: every customer document carries the logo. */
  logoUrl: string;
  bankDetails?: {
    bankName: string;
    accountName?: string;
    accountNumber: string;
    branchCode?: string;
    accountType?: string;
    swiftCode?: string;
    reference?: string;
  };
}

/** The bill-to party. Sourced from `customer_profile` row. */
export interface InvoicePDFCustomer {
  name: string;
  contactPerson?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  email?: string;
  phone?: string;
  /** Customer's own VAT number, if registered. Displayed informational only. */
  vatNumber?: string;
  registrationNumber?: string;
}

export interface InvoicePDFLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  /** VAT rate as decimal: 0 for non-VAT, 0.15 for SA standard. */
  vatRate: number;
  /** Reference for traceability (e.g. cpanel_username, domain name). */
  reference?: string;
}

export interface InvoicePDFInvoice {
  number: string;
  issueDate: string;
  dueDate?: string;
  paymentTerms?: string;
  reference?: string;
  /** Line totals — pre-computed by caller for consistency. */
  subtotal: number;
  vatAmount: number;
  total: number;
  notes?: string;
  /** Status text printed in the header block (e.g. "PAID", "OVERDUE"). */
  statusLabel?: string;
  /** QM (2026-07-17): overrides the masthead — e.g. "QUOTATION". When unset,
   *  falls back to INVOICE / TAX INVOICE from company.vatRegistered. */
  documentTitle?: string;
  /** Small line under the number, e.g. "Valid until 31 July 2026" or
   *  "Ref: quotation QUO-2026-000001". */
  documentSubtitle?: string;
  /** Extra first line in the fixed footer, e.g. the quotation
   *  not-a-demand-for-payment disclaimer. */
  footerNote?: string;
}

export interface InvoiceDocumentProps {
  company: InvoicePDFCompany;
  customer: InvoicePDFCustomer;
  invoice: InvoicePDFInvoice;
  lineItems: InvoicePDFLineItem[];
  /** When present, renders a one-click PayFast checkout box on the PDF.
   *  Caller is responsible for only passing this for unpaid invoices. */
  payNow?: InvoicePDFPayNow;
}

/**
 * Render the invoice as a `@react-pdf/renderer` Document tree.
 *
 * Wrap with `<PDFViewer>` for browser preview, or pass to `pdf()` to materialise
 * a Blob/Buffer for download.
 */
export const InvoiceDocument: React.FC<InvoiceDocumentProps> = ({
  company,
  customer,
  invoice,
  lineItems,
  payNow,
}) => {
  const headerLabel =
    invoice.documentTitle ?? (company.vatRegistered ? 'TAX INVOICE' : 'INVOICE');
  const showVatColumn = company.vatRegistered;

  // Build line-items table data.
  const tableData = lineItems.map((item) => {
    const lineTotal = item.quantity * item.unitPrice;
    const row: Record<string, string> = {
      description: item.description + (item.reference ? `\n${item.reference}` : ''),
      qty: item.quantity.toLocaleString('en-ZA'),
      unitPrice: formatPDFCurrency(item.unitPrice),
      total: formatPDFCurrency(lineTotal),
    };
    if (showVatColumn) {
      row.vat = `${(item.vatRate * 100).toFixed(0)}%`;
    }
    return row;
  });

  const tableColumns: TableColumn[] = showVatColumn
    ? [
        { key: 'description', header: 'Description', width: '46%', align: 'left' },
        { key: 'qty', header: 'Qty', width: '10%', align: 'center' },
        { key: 'unitPrice', header: 'Unit Price', width: '17%', align: 'right' },
        { key: 'vat', header: 'VAT', width: '10%', align: 'center' },
        { key: 'total', header: 'Total', width: '17%', align: 'right' },
      ]
    : [
        { key: 'description', header: 'Description', width: '54%', align: 'left' },
        { key: 'qty', header: 'Qty', width: '12%', align: 'center' },
        { key: 'unitPrice', header: 'Unit Price', width: '17%', align: 'right' },
        { key: 'total', header: 'Total', width: '17%', align: 'right' },
      ];

  return (
    <Document>
      <Page size="A4" style={documentStyles.page}>
        {/* HEADER — logo + document type */}
        <View style={documentStyles.header}>
          <View>
            {company.logoUrl ? (
              <Image src={company.logoUrl} style={documentStyles.logo} />
            ) : (
              <Text style={[documentStyles.companyName, { fontSize: 16 }]}>
                {company.companyName}
              </Text>
            )}
          </View>
          <View>
            <Text style={documentStyles.documentTitle}>{headerLabel}</Text>
            <Text style={documentStyles.documentNumber}>{invoice.number}</Text>
            {invoice.documentSubtitle && (
              <Text style={documentStyles.documentNumber}>
                {invoice.documentSubtitle}
              </Text>
            )}
            {invoice.statusLabel && (
              <Text
                style={[
                  documentStyles.documentNumber,
                  { fontFamily: 'Helvetica-Bold', marginTop: 4 },
                ]}
              >
                {invoice.statusLabel}
              </Text>
            )}
          </View>
        </View>

        {/* ADDRESS GRID */}
        <View style={documentStyles.addressGrid}>
          {/* From — SightHost */}
          <View style={documentStyles.addressColumn}>
            <Text style={documentStyles.addressLabel}>From</Text>
            <Text style={documentStyles.companyName}>
              {company.legalName ?? company.companyName}
            </Text>
            {company.address && (
              <Text style={documentStyles.addressLine}>{company.address}</Text>
            )}
            {(company.city || company.postalCode) && (
              <Text style={documentStyles.addressLine}>
                {[company.city, company.postalCode].filter(Boolean).join(', ')}
              </Text>
            )}
            {company.phone && (
              <Text style={documentStyles.addressLine}>{company.phone}</Text>
            )}
            {company.email && (
              <Text style={documentStyles.addressLine}>{company.email}</Text>
            )}
            {company.registrationNumber && (
              <Text style={documentStyles.addressLine}>
                Reg: {company.registrationNumber}
              </Text>
            )}
            {company.vatRegistered && company.vatNumber && (
              <Text style={documentStyles.addressLine}>
                VAT: {company.vatNumber}
              </Text>
            )}
          </View>

          {/* Bill To — Customer */}
          <View style={documentStyles.addressColumn}>
            <Text style={documentStyles.addressLabel}>Bill To</Text>
            <Text style={documentStyles.companyName}>{customer.name}</Text>
            {customer.contactPerson && (
              <Text style={documentStyles.addressLine}>
                Att: {customer.contactPerson}
              </Text>
            )}
            {customer.address && (
              <Text style={documentStyles.addressLine}>{customer.address}</Text>
            )}
            {(customer.city || customer.postalCode) && (
              <Text style={documentStyles.addressLine}>
                {[customer.city, customer.postalCode].filter(Boolean).join(', ')}
              </Text>
            )}
            {customer.email && (
              <Text style={documentStyles.addressLine}>{customer.email}</Text>
            )}
            {customer.registrationNumber && (
              <Text style={documentStyles.addressLine}>
                Reg: {customer.registrationNumber}
              </Text>
            )}
            {customer.vatNumber && (
              <Text style={documentStyles.addressLine}>
                VAT: {customer.vatNumber}
              </Text>
            )}
          </View>
        </View>

        {/* DATES */}
        <View style={documentStyles.datesSection}>
          <View style={documentStyles.dateBlock}>
            <Text style={documentStyles.dateLabel}>Issue date</Text>
            <Text style={documentStyles.dateValue}>{formatPDFDate(invoice.issueDate)}</Text>
          </View>
          {invoice.dueDate && (
            <View style={documentStyles.dateBlock}>
              <Text style={documentStyles.dateLabel}>Due date</Text>
              <Text style={documentStyles.dateValue}>{formatPDFDate(invoice.dueDate)}</Text>
            </View>
          )}
          <View style={documentStyles.dateBlock}>
            <Text style={documentStyles.dateLabel}>Payment terms</Text>
            <Text style={documentStyles.dateValue}>
              {invoice.paymentTerms ?? 'Net 30 days'}
            </Text>
          </View>
          {invoice.reference && (
            <View style={documentStyles.dateBlock}>
              <Text style={documentStyles.dateLabel}>Reference</Text>
              <Text style={documentStyles.dateValue}>{invoice.reference}</Text>
            </View>
          )}
        </View>

        {/* LINE ITEMS */}
        <PDFTable columns={tableColumns} data={tableData} />

        {/* TOTALS */}
        <View style={documentStyles.totalsSection}>
          {showVatColumn ? (
            <>
              <View style={documentStyles.totalsRow}>
                <Text style={documentStyles.totalsLabel}>Subtotal</Text>
                <Text style={documentStyles.totalsValue}>
                  {formatPDFCurrency(invoice.subtotal)}
                </Text>
              </View>
              <View style={documentStyles.totalsRow}>
                <Text style={documentStyles.totalsLabel}>VAT</Text>
                <Text style={documentStyles.totalsValue}>
                  {formatPDFCurrency(invoice.vatAmount)}
                </Text>
              </View>
              <View style={documentStyles.totalsRow}>
                <Text style={[documentStyles.totalsLabel, documentStyles.totalsFinal]}>
                  Total
                </Text>
                <Text style={[documentStyles.totalsValue, documentStyles.totalsFinal]}>
                  {formatPDFCurrency(invoice.total)}
                </Text>
              </View>
            </>
          ) : (
            <View style={documentStyles.totalsRow}>
              <Text style={[documentStyles.totalsLabel, documentStyles.totalsFinal]}>
                Total due
              </Text>
              <Text style={[documentStyles.totalsValue, documentStyles.totalsFinal]}>
                {formatPDFCurrency(invoice.total)}
              </Text>
            </View>
          )}
        </View>

        {/* PAY NOW — one-click PayFast checkout (unpaid invoices only) */}
        {payNow && (
          <View style={payNowStyles.box} wrap={false}>
            <Text style={payNowStyles.title}>
              Pay {payNow.amountDisplay} by card — instant
            </Text>
            <Text style={payNowStyles.body}>
              Tap the link below for one-click checkout via PayFast. No portal
              sign-in required, accepts Visa / Mastercard / Instant EFT.
            </Text>
            <Link src={payNow.url} style={payNowStyles.link}>
              {payNow.url}
            </Link>
            <Text style={payNowStyles.hint}>
              Or pay by bank transfer using the details below — payment must
              match the invoice total exactly.
            </Text>
          </View>
        )}

        {/* PAYMENT DETAILS */}
        {company.bankDetails && (
          <View style={documentStyles.paymentSection}>
            <Text style={documentStyles.sectionTitle}>Payment details</Text>
            <Text style={documentStyles.paymentDetail}>
              Bank: {company.bankDetails.bankName}
            </Text>
            {company.bankDetails.accountName && (
              <Text style={documentStyles.paymentDetail}>
                Account name: {company.bankDetails.accountName}
              </Text>
            )}
            <Text style={documentStyles.paymentDetail}>
              Account number: {company.bankDetails.accountNumber}
            </Text>
            {company.bankDetails.branchCode && (
              <Text style={documentStyles.paymentDetail}>
                Branch code: {company.bankDetails.branchCode}
              </Text>
            )}
            {company.bankDetails.accountType && (
              <Text style={documentStyles.paymentDetail}>
                Account type: {company.bankDetails.accountType}
              </Text>
            )}
            <Text
              style={[
                documentStyles.paymentDetail,
                { marginTop: 4, fontFamily: 'Helvetica-Bold' },
              ]}
            >
              Reference: {company.bankDetails.reference ?? invoice.number}
            </Text>
          </View>
        )}

        {/* NOTES */}
        {invoice.notes && (
          <View style={documentStyles.paymentSection}>
            <Text style={documentStyles.sectionTitle}>Notes</Text>
            {invoice.notes.split('\n').map((line, i) => (
              <Text key={i} style={documentStyles.paymentDetail}>
                {line}
              </Text>
            ))}
          </View>
        )}

        {/* FOOTER */}
        <View style={documentStyles.footer} fixed>
          {invoice.footerNote && (
            <Text style={documentStyles.footerText}>{invoice.footerNote}</Text>
          )}
          <Text style={documentStyles.footerText}>Thank you for your business.</Text>
          <Text style={documentStyles.footerText}>
            {company.legalName ?? company.companyName}
            {company.registrationNumber && `  ·  Reg ${company.registrationNumber}`}
            {company.vatRegistered && company.vatNumber && `  ·  VAT ${company.vatNumber}`}
          </Text>
          <Text style={documentStyles.footerCopyright}>
            © {new Date().getFullYear()} {company.companyName}.
            {!company.vatRegistered && ' Not a tax invoice — SightHost is not registered for VAT.'}
          </Text>
        </View>
      </Page>
    </Document>
  );
};
